<template>
    <div class="vx-dashboard relative">
        <div class="w-full h-full relative" id="dashboard-app">
            <!-- <control-menu
                class="widget-picker"
                :unlocked.sync="isEditable"
                :widgets="supportedComponents_x"
                :is-modified="isModified"
                :is-empty="!widgets.any()"
                :disabled="loading"
                @reset="resetChanges"
                @clear="emptyDashboard"
                @save="saveDashboard"
                @dragstart="dragstart"
            /> -->
            <grid-layout
                class="w-full"
                ref="gridLayout"
                :layout="widgets"
                :col-num="colNum"
                :row-height="rowHeight"
                :vertical-compact="true"
                :is-draggable="isEditable"
                :is-resizable="isEditable"
                :is-mirrored="false"
                :prevent-collision="false"
                :margin="margin_x"
                :use-css-transforms="true"
                style="min-height: calc(var(--vh, 1vh) * 100 - 11.5rem)"
            >
                <grid-item
                    v-for="gridItem in gridItems"
                    v-bind="gridItem.widget"
                    class="grid-item"
                    drag-allow-from=".vue-draggable-handle"
                    drag-ignore-from=".no-drag"
                    :style="widgetsZIndex[gridItem.widget.i] || defaultZIndex"
                    :key="gridItem.widget.i"
                    :class="{ editable: isEditable }"
                    :is-resizable="isEditable && gridItem.isResizable"
                >
                    <component
                        ref="widget"
                        :is="gridItem.widget.component"
                        :is-editable="isEditable"
                        :scale="scale"
                        :widget="gridItem.widget"
                        :config="gridItem.widget.config"
                        @remove="removeWidget"
                        @config-updated="(v) => updateWidget(gridItem.widget, v)"
                        @dragstart="dragstart"
                    />
                </grid-item>
            </grid-layout>
        </div>
    </div>
</template>

<script>
import { GridLayout, GridItem } from 'vue-grid-layout'
import { mapState, mapGetters } from 'vuex'
import CustomResize from './CustomResize.vue'
// import ControlMenu from './ControlMenu.vue'
import dashboardStore from '../../../store/dashboard/index.js'
import { createWidgetInstance } from './widgetHelpers.js'
import { findComponent } from '../../../assets/utils/components'

const COL_WIDTH_CONSTANT = 25

export default {
    name: 'vx-dashboard',
    components: {
        GridLayout,
        GridItem,
        CustomResize
        // ControlMenu
    },
    props: {
        id: {
            type: String
        },
        colNum: {
            type: [Number, String],
            default: 60
        },
        type: {
            type: String
        },
        supportedComponents: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            newDashboardId: null,
            isEditable: false,
            margin: [10, 10],
            lastTime: 0,
            availableComponents: {},
            dragPosition: {
                x: null,
                y: null,
                w: 1,
                h: 1,
                i: null
            },
            currentDashboard: {
                id: '',
                name: 'Untitled',
                type: '',
                widgets: []
            },
            gridRectangle: {
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                width: 0,
                height: 0,
                x: 0,
                y: 0
            },
            widgetsZIndex: {},
            defaultZIndex: 'z-index:auto'
        }
    },
    watch: {
        id_x: {
            handler: async function (newId) {
                this.$nextTick(async () => {
                    if (!newId) {
                        await this.createNewDashboard()
                        return
                    }
                    this.$store.dispatch('dashboard/fetchDashboard', newId)
                })
            },
            immediate: true
        },
        originalDashboard: {
            handler: async function (newDashboard) {
                if (!newDashboard) {
                    return
                }

                // Prevents reassignment and regeneration, and initialization
                // of all widgets
                if (!this.isModified && this.currentDashboard.id) {
                    return
                }
                this.currentDashboard = this.deepCopy(newDashboard)
            },
            immediate: true,
            deep: true
        },
        windowWidth: {
            handler: function (v) {
                this.setGridRectangle()
            }
        }
    },
    computed: {
        ...mapState(['windowWidth']),
        ...mapGetters({
            getDashboardById: 'dashboard/getDashboardById',
            currentDraggedWidget: 'dashboard/currentDraggedWidget',
            getDashboardIdForType: 'dashboard/getActiveDashboardIdByType'
        }),
        supportedComponents_x() {
            return Object.values(this.availableComponents)
        },
        gridItems() {
            return this.widgets.map((widget, index) => {
                var isResizable = false
                var componentName = widget.component

                if (componentName && this.availableComponents[componentName]) {
                    var component = this.availableComponents[componentName]
                    var instance = component.instance
                    isResizable = instance.isResizable
                }

                return {
                    widget,
                    isResizable
                }
            })
        },
        maxRowNumber() {
            return this.widgets.max((item) => item.y)
        },
        margin_x() {
            return this.margin.map((v) => v * this.scale)
        },
        id_x() {
            // When a new dashboard is created for a type, if the parent
            // did not sync map the id prop, id_x would never reflect the new
            // id for the table and it would never display
            if (this.newDashboardId) {
                return this.newDashboardId
            }
            if (this.id) {
                return this.id
            }
            if (this.type) {
                return this.getDashboardIdForType(this.type)
            }
            return null
        },
        grid() {
            return this.$refs.gridLayout
        },
        originalDashboard() {
            return this.getDashboardById(this.id_x)
        },
        originalWidgetString() {
            if (!this.originalDashboard) {
                return '[]'
            }
            return JSON.stringify(this.originalDashboard.widgets)
        },
        currentWidgetString() {
            return JSON.stringify(this.widgets)
        },
        isModified() {
            return this.originalWidgetString != this.currentWidgetString
        },
        widgets: {
            get() {
                return this.currentDashboard.widgets
            },
            set(v) {
                this.currentDashboard.widgets = v
            }
        },
        fakeItem() {
            return this.widgets.first((item) => item.i === 'drop')
        },
        gridWidth() {
            return this.gridRectangle.width
        },
        rowHeight() {
            // calculation taken from inside vue-grid-item
            // using margin x as it factors in the scaling, this can't be done
            // for columnWidth as this creates an infinite loop with scale calcs
            return (this.gridWidth - this.margin_x[0] * (this.colNum + 1)) / this.colNum
        },
        columnWidth() {
            // calculation taken from inside vue-grid-item
            return (this.gridWidth - this.margin[0]) / this.colNum
        },
        scale() {
            return this.columnWidth / COL_WIDTH_CONSTANT
        }
    },
    methods: {
        async syncTypeLookups() {
            try {
                this.loading = true
                await this.$store.dispatch('dashboard/sync')
            } catch (error) {
                this.notifyServerError(error)
            } finally {
                this.loading = false
            }
        },
        dragstart(e) {
            document.addEventListener('dragover', this.dragover)
            document.addEventListener('drop', this.drop)
        },
        resetChanges() {
            this.currentDashboard = this.deepCopy(this.originalDashboard)
        },
        emptyDashboard() {
            this.widgets = []
            this.widgetsZIndex = {}
        },
        updateWidget(widget, config) {
            this.$set(widget, 'config', config)
        },
        async createNewDashboard() {
            await this.syncTypeLookups()
            if (this.id_x) {
                return
            }

            // Dashboard should be inserted  on the database after creation so that
            // we can manipulate it afterwards or else
            // it throws no record found upon saving...
            var dashboard = await this.$store.dispatch('dashboard/createNewDashboard', {
                id: this.id_x,
                type: this.type,
                name: 'New Dashboard'
            })
            this.newDashboardId = dashboard.id
            if (dashboard.id != this.id_x) {
                this.$emit('update:id', dashboard.id)
            }
            this.$emit('dashboard-created', dashboard)
        },
        async saveDashboard() {
            if (!this.isModified) {
                this.isEditable = false
                return
            }
            try {
                this.loading = true
                await this.$store.dispatch('dashboard/saveDashboard', this.currentDashboard)
                this.recreateZIndex(this.currentDashboard)
                this.notifySuccess('Success', `Saved Dashboard Successfully`, 5000)
                this.isEditable = false
            } catch (error) {
                this.notifyServerError(error)
            } finally {
                this.loading = false
            }
        },
        getFakeItemIndex() {
            return this.widgets.findIndex((item) => item.i === 'drop')
        },
        dragover(e) {
            if (!this.isWidgetEvent(e)) {
                return
            }
            e.preventDefault()
            if (this.lastTime && e.timeStamp - this.lastTime < 50) {
                return
            }
            this.lastTime = e.timeStamp
            if (!this.fakeItem) {
                this.createFakeElement()
            }
            this.moveFakeElement(e)
        },
        moveFakeElement(e) {
            let index = this.getFakeItemIndex()
            try {
                this.grid.$children[this.widgets.length].$refs.item.style.display = 'none'
            } catch {}

            let directChild = this.grid.$children[index]
            var el = findComponent(directChild, (child) => child.calcXY)
            var widgetWidthPx = this.columnWidth * this.currentDraggedWidget.w
            var widgetHeightPx = this.rowHeight * this.currentDraggedWidget.h

            var dragCoords = {
                top: e.clientY - this.gridRectangle.top - widgetHeightPx / 2,
                left: e.clientX - this.gridRectangle.left - widgetWidthPx / 2
            }

            el.dragging = dragCoords
            this.dragPosition = el.calcXY(dragCoords.top, dragCoords.left)

            this.grid.dragEvent(
                'dragstart',
                'drop',
                this.dragPosition.x,
                this.dragPosition.y,
                this.currentDraggedWidget.h,
                this.currentDraggedWidget.w
            )
        },
        createFakeElement(e) {
            this.setGridRectangle()
            var config = this.currentDraggedWidget
            this.widgets.push({
                x: (this.widgets.length * 2) % this.colNum,
                y: this.widgets.length + this.colNum,
                w: config.w,
                h: config.h,
                i: 'drop'
            })
        },
        destroyFakeElement(e) {
            var config = this.currentDraggedWidget
            this.grid.dragEvent(
                'dragend',
                'drop',
                this.dragPosition.x,
                this.dragPosition.y,
                config.h,
                config.w
            )
            this.widgets.remove(this.fakeItem)
        },
        drop(e) {
            document.removeEventListener('dragover', this.dragover)
            if (!this.isWidgetEvent(e)) {
                return
            }
            //add the actual element in place of the fake one
            var widget = this.currentDraggedWidget
            var lastKnownPosition = this.fakeItem
            this.destroyFakeElement()

            var newWidget = {
                i: window.generateGuid(),
                w: widget.w,
                h: widget.h,
                x: lastKnownPosition.x,
                y: lastKnownPosition.y,
                isResizable: widget.isResizable,
                component: widget.component,
                config: widget.defaultConfig || {}
            }
            //change it to store
            this.$store.commit('dashboard/SET_CURRENT_DRAGGED_WIDGET', null)
            this.widgets.push(newWidget)
            try {
                this.grid.$children[this.widgets.length].$refs.item.style.display = 'block'
            } catch {}
        },
        removeWidget(widget) {
            this.widgets.remove(widget)
        },
        setGridRectangle() {
            if (!this.grid) {
                return
            }
            var rect = this.grid.$el.getBoundingClientRect()
            Object.assign(this.gridRectangle, {
                left: rect.left,
                right: rect.right,
                top: rect.top,
                bottom: rect.bottom,
                width: rect.width,
                height: rect.height,
                x: rect.x,
                y: rect.y
            })
        },
        async confirmLeave() {
            // called from refs by parent route component
            // usually called during beforeRouteLeave
            if (!this.isModified) {
                return Promise.resolve(true)
            }
            return this.confirm({
                title: 'Are You Sure?',
                acceptText: 'Leave',
                text: 'There are unsaved changes to this dashboard. If you continue any unsaved changes will be lost. Leave?'
            })
        },
        isWidgetEvent(e) {
            return e && e.dataTransfer.types[0] == 'widget'
        },
        createZIndex(widget) {
            var component = this.availableComponents[widget.component]
            if (!component) {
                return
            }
            var instance = component.instance
            var overflows = instance.overflowEnabled
            var zIndex = 'auto'
            if (overflows) {
                var rowOffset = this.maxRowNumber - widget.y
                zIndex = rowOffset + 1
            }
            this.widgetsZIndex[widget.i] = `z-index:${zIndex}`
        },
        recreateZIndex(dashboard) {
            if (!dashboard) {
                return
            }
            this.widgetsZIndex = {}
            dashboard.widgets.forEach((widget) => {
                this.createZIndex(widget)
            })
        }
    },
    created() {
        for (let c = 0; c < this.supportedComponents.length; c++) {
            var component = this.supportedComponents[c]
            var instance = createWidgetInstance(component)
            var name = component.name

            this.availableComponents[name] = {
                instance,
                component,
                name: instance.$options.propsData.title
            }
            this.$options.components[component.name] = component
        }
    },
    async beforeCreate() {
        this.$store.register(dashboardStore)
    },
    beforeDestroy() {
        document.removeEventListener('dragover', this.dragover)
        document.removeEventListener('drop', this.drop)
        this.$store.commit('dashboard/CLEAR')
        this.$store.unregister(dashboardStore)
    },
    mounted() {
        // if (!this.originalDashboard) {
        this.recreateZIndex(this.originalDashboard)
        // }
        this.setGridRectangle()
    }
}
</script>

<style scoped lang="scss">
.vue-grid-layout {
    background: transparent;
}
.grid-item {
    touch-action: none;
    &:hover.editable {
        z-index: 100 !important;
    }
}
</style>
